import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import styled from 'styled-components';
import {
  apiErrorStatus,
  apiErrorText,
  apiLoadErrorText,
  useArchiveFleetGroupMutation,
  useGetFleetGroupQuery,
  useRestoreFleetGroupMutation,
  type FleetGroup,
  type FleetGroupDetail,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerFooter,
  DrawerHeader,
} from '@shared/ui/Drawer';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { IconArrowRight, IconPencil } from '@shared/ui/icons';
import {
  Actions,
  ButtonLink,
  FleetChip,
  FooterStatus,
  GroupUnitChip,
  Notice,
  TextButton,
  fleetLeading,
  fleetType,
  formatDateTime,
  typo,
} from '@entities/fleet';
import { GroupForm } from './GroupForm';
import { GroupView } from './GroupView';

/** Значение `?group=` для новой группы. */
export const NEW_GROUP = 'new';

type Mode = 'view' | 'edit';

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  min-height: 36px;
`;

const Eyebrow = styled.p`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-size: ${fleetType.micro};
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Title = styled.h2`
  margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
  font-size: ${fleetType.title};
  font-weight: 600;
  line-height: ${fleetLeading.tight};
  letter-spacing: -0.01em;
  text-wrap: balance;
  overflow-wrap: anywhere;

  /* Фокус при открытии — чтобы скринридер прочёл название; это не элемент управления. */
  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  margin-top: ${({ theme }) => theme.spacing(2.5)};
  padding-bottom: ${({ theme }) => theme.spacing(4)};

  & > .text {
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

function BodySkeleton() {
  return (
    <DrawerBody aria-busy="true">
      <SkeletonLine $height={64} />
      <SkeletonLine $width="45%" />
      <SkeletonLine $width="85%" />
      <SkeletonLine $width="70%" />
    </DrawerBody>
  );
}

function ConfirmArchive({
  busy,
  onConfirm,
  onCancel,
}: {
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    confirmRef.current?.focus();
  }, []);
  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    }
  }
  return (
    <Actions role="group" aria-label="Подтверждение архивации" onKeyDown={onKeyDown}>
      <FooterStatus>
        {typo(
          'Отправить группу в архив? Модели и должности Хронума освободятся для других групп, ставки группы останутся в истории.',
        )}
      </FooterStatus>
      <Button ref={confirmRef} type="button" $variant="danger" disabled={busy} onClick={onConfirm}>
        {busy ? 'Отправляю…' : 'В архив'}
      </Button>
      <Button type="button" $variant="ghost" disabled={busy} onClick={onCancel}>
        Отмена
      </Button>
    </Actions>
  );
}

interface ViewFooterProps {
  detail: FleetGroupDetail;
  ratesHref: string | null;
  flash: string | null;
  onEdit: () => void;
}

/** Действия карточки: ставки группы, архив, правка. */
function ViewFooter({ detail, ratesHref, flash, onEdit }: ViewFooterProps) {
  const { group } = detail;
  const [confirming, setConfirming] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNote, setActionNote] = useState<string | null>(null);
  const [archive, { isLoading: archiving }] = useArchiveFleetGroupMutation();
  const [restore, { isLoading: restoring }] = useRestoreFleetGroupMutation();
  const archiveRef = useRef<HTMLButtonElement>(null);

  async function doArchive() {
    setActionError(null);
    try {
      await archive(group.id).unwrap();
      setConfirming(false);
      setActionNote('Группа в архиве');
    } catch (err) {
      setActionError(apiErrorText(err, 'Не удалось отправить группу в архив.'));
    }
  }

  async function doRestore() {
    setActionError(null);
    try {
      await restore(group.id).unwrap();
      setActionNote(
        typo(
          'Группа возвращена. Модели и должности Хронума добавьте заново — при архивации они освободились.',
        ),
      );
    } catch (err) {
      setActionError(
        apiErrorStatus(err) === 409
          ? apiErrorText(err, 'Вернуть нельзя: группа с таким названием уже есть.')
          : apiErrorText(err, 'Не удалось вернуть группу из архива.'),
      );
    }
  }

  function cancelConfirm() {
    setConfirming(false);
    requestAnimationFrame(() => archiveRef.current?.focus());
  }

  if (confirming) {
    return (
      <DrawerFooter>
        <ConfirmArchive
          busy={archiving}
          onConfirm={() => void doArchive()}
          onCancel={cancelConfirm}
        />
      </DrawerFooter>
    );
  }
  return (
    <DrawerFooter>
      <FooterStatus role="status">{actionError ?? actionNote ?? flash ?? ''}</FooterStatus>
      <Actions>
        {ratesHref && !group.archived && (
          <ButtonLink to={ratesHref} $variant="ghost">
            Ставки группы
            <IconArrowRight size={16} />
          </ButtonLink>
        )}
        {group.archived ? (
          <Button
            type="button"
            $variant="secondary"
            disabled={restoring}
            onClick={() => void doRestore()}
          >
            {restoring ? 'Возвращаю…' : 'Вернуть из архива'}
          </Button>
        ) : (
          <>
            <Button
              ref={archiveRef}
              type="button"
              $variant="ghost"
              onClick={() => {
                setActionError(null);
                setConfirming(true);
              }}
            >
              В архив
            </Button>
            <Button type="button" $variant="primary" onClick={onEdit}>
              <IconPencil size={16} />
              Изменить
            </Button>
          </>
        )}
      </Actions>
    </DrawerFooter>
  );
}

interface GroupDrawerProps {
  /** id группы или `NEW_GROUP`. */
  groupId: string;
  /** Строка списка — шапка видна сразу, пока грузится карточка. */
  preview: FleetGroup | undefined;
  canWrite: boolean;
  /** Ссылка на ставки группы; `null` — у человека нет доступа к Экономисту. */
  ratesHref: (groupId: string) => string | null;
  onClose: () => void;
  onCreated: (id: string) => void;
}

/**
 * Карточка группы механизаторов в боковой панели: учёт, должности Хронума,
 * модели с действующей ПРЦ; правка — в той же панели.
 */
export function GroupDrawer({
  groupId,
  preview,
  canWrite,
  ratesHref,
  onClose,
  onCreated,
}: GroupDrawerProps) {
  const isNew = groupId === NEW_GROUP;
  const uid = useId();
  const titleId = `${uid}-title`;
  const formId = `${uid}-form`;
  const titleRef = useRef<HTMLHeadingElement>(null);
  const dirtyRef = useRef(0);

  const { data, error } = useGetFleetGroupQuery(groupId, { skip: isNew });
  const current = data && data.group.id === groupId ? data : undefined;
  const group = current?.group ?? preview;

  const [ui, setUi] = useState<{ id: string; mode: Mode; session: number; flash: string | null }>(
    () => ({ id: groupId, mode: isNew ? 'edit' : 'view', session: 0, flash: null }),
  );
  if (ui.id !== groupId) {
    setUi({
      id: groupId,
      mode: isNew ? 'edit' : 'view',
      session: ui.session + 1,
      flash: ui.id === NEW_GROUP ? ui.flash : null,
    });
  }
  const editing = ui.mode === 'edit';

  const onDirtyChange = useCallback((count: number) => {
    dirtyRef.current = count;
  }, []);

  const confirmDiscard = useCallback(() => {
    const n = dirtyRef.current;
    return n === 0 || window.confirm(`Закрыть без сохранения? Правок: ${n} — они пропадут.`);
  }, []);

  const requestClose = useCallback(() => {
    if (!confirmDiscard()) return;
    dirtyRef.current = 0;
    onClose();
  }, [confirmDiscard, onClose]);

  const focusTitle = () =>
    requestAnimationFrame(() => titleRef.current?.focus({ preventScroll: true }));

  function cancelEdit() {
    if (!confirmDiscard()) return;
    dirtyRef.current = 0;
    if (isNew) {
      onClose();
      return;
    }
    setUi((s) => ({ ...s, mode: 'view' }));
    focusTitle();
  }

  function saved(result: FleetGroupDetail, note: string | null) {
    dirtyRef.current = 0;
    const flash = [isNew ? 'Группа добавлена' : 'Изменения сохранены', note]
      .filter(Boolean)
      .join('. ');
    if (isNew) {
      setUi((s) => ({ ...s, flash }));
      onCreated(result.group.id);
    } else {
      setUi((s) => ({ ...s, mode: 'view', flash }));
    }
    focusTitle();
  }

  const notFound = !isNew && error !== undefined && apiErrorStatus(error) === 404;
  const rates = isNew ? null : ratesHref(groupId);

  return (
    <Drawer open onClose={requestClose} labelledBy={titleId} initialFocusRef={titleRef}>
      <DrawerHeader>
        <HeaderTop>
          <Eyebrow>
            {isNew ? 'Новая группа' : editing ? 'Правка группы' : 'Группа механизаторов'}
          </Eyebrow>
          <DrawerCloseButton onClick={requestClose} />
        </HeaderTop>
        <Title id={titleId} ref={titleRef} tabIndex={-1}>
          {isNew ? 'Новая группа механизаторов' : (group?.name ?? 'Группа механизаторов')}
        </Title>
        <MetaRow>
          {group && !isNew ? (
            <>
              <GroupUnitChip unit={group.unit} />
              {group.archived && <FleetChip $tone="muted">в архиве</FleetChip>}
              <span className="text">изменена {formatDateTime(group.updated_at)}</span>
            </>
          ) : (
            <span className="text">
              {typo(
                'Группа — профессия механизатора, которую знает Хронум. Достаточно названия — должности и модели можно добавить потом.',
              )}
            </span>
          )}
        </MetaRow>
      </DrawerHeader>

      {isNew ? (
        <GroupForm
          key={`new-${ui.session}`}
          detail={null}
          formId={formId}
          onCancel={cancelEdit}
          onSaved={saved}
          onDirtyChange={onDirtyChange}
        />
      ) : notFound ? (
        <DrawerBody>
          <Notice $tone="danger" role="alert">
            <span className="text">{typo('Группа не найдена — возможно, ссылка устарела.')}</span>
            <TextButton type="button" onClick={onClose}>
              К списку
            </TextButton>
          </Notice>
        </DrawerBody>
      ) : error !== undefined && !current ? (
        <DrawerBody>
          <Notice $tone="danger" role="alert">
            <span className="text">
              {apiLoadErrorText(
                error,
                'Не удалось открыть группу.',
                'Нет доступа к справочнику техники.',
              )}
            </span>
          </Notice>
        </DrawerBody>
      ) : !current ? (
        <BodySkeleton />
      ) : editing ? (
        <GroupForm
          key={`${groupId}-${ui.session}`}
          detail={current}
          formId={formId}
          onCancel={cancelEdit}
          onSaved={saved}
          onDirtyChange={onDirtyChange}
        />
      ) : (
        <>
          <DrawerBody>
            <GroupView detail={current} idPrefix={uid} />
          </DrawerBody>
          {canWrite ? (
            <ViewFooter
              key={groupId}
              detail={current}
              ratesHref={rates}
              flash={ui.flash}
              onEdit={() =>
                setUi((s) => ({ ...s, mode: 'edit', session: s.session + 1, flash: null }))
              }
            />
          ) : (
            rates && (
              <DrawerFooter>
                <FooterStatus />
                <ButtonLink to={rates} $variant="ghost">
                  Ставки группы
                  <IconArrowRight size={16} />
                </ButtonLink>
              </DrawerFooter>
            )
          )}
        </>
      )}
    </Drawer>
  );
}
